import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Table, Btn } from "@/components/UI";
import { Plus } from "lucide-react";
import { ExplicitFilterBar } from "@/components/common/ExplicitFilterBar";
import { OrderStatusBadge, StockStatusBadge, DeliveryStatus } from "@/components/sales/OrderBadges";
import { OrderActions } from "@/components/sales/OrderActions";
import { useSalesCapabilities } from "@/lib/useSalesCapabilities";
import { SO_STATUS, SO_STOCK, SO_SOURCES, SOURCE_LABEL, trDate } from "@/lib/sales";
import { fmtNum, toQuery } from "@/lib/products";
import { apiError } from "@/lib/customers";

const INITIAL = { q: "", status: "", source: "", stock_status: "", customer_id: "", date_from: "", date_to: "" };
const opt = (obj, labelOf) => [{ value: "", label: "Tümü" }, ...Object.keys(obj).map((k) => ({ value: k, label: labelOf(k) }))];

export default function SalesOrderListPage() {
  const nav = useNavigate();
  const caps = useSalesCapabilities();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [filters, setFilters] = useState(INITIAL);
  const [err, setErr] = useState("");
  const load = (f = filters) => api.get(`/sales-orders${toQuery(f)}`).then((r) => { setRows(r); setErr(""); })
    .catch((e) => setErr(apiError(e, "Siparişler yüklenemedi")));
  useEffect(() => {
    load(INITIAL);
    api.get("/customers").then(setCustomers).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fields = [
    { key: "q", label: "Ara (No / Müşteri / Ref.)", width: "w-60" },
    { key: "customer_id", label: "Müşteri", type: "select", width: "w-48", options: [{ value: "", label: "Tümü" }, ...customers.map((c) => ({ value: c.id, label: c.legal_name }))] },
    { key: "status", label: "Durum", type: "select", width: "w-36", options: opt(SO_STATUS, (k) => SO_STATUS[k].label) },
    { key: "source", label: "Kaynak", type: "select", width: "w-32", options: opt(Object.fromEntries(SO_SOURCES.map((s) => [s, 1])), (k) => SOURCE_LABEL[k]) },
    { key: "stock_status", label: "Stok Durumu", type: "select", width: "w-36", options: opt(SO_STOCK, (k) => SO_STOCK[k].label) },
    { key: "date_from", label: "Tarih Başlangıç", type: "date", width: "w-36" },
    { key: "date_to", label: "Tarih Bitiş", type: "date", width: "w-36" },
  ];

  return (
    <div data-testid="sales-orders-page">
      <PageHeader title="Satış Siparişleri" description={`${rows.length} sipariş`}
        actions={caps.create && <Btn onClick={() => nav("/sales-orders/new")} data-testid="new-so-btn"><Plus size={14} /> Yeni Sipariş</Btn>} />
      <ExplicitFilterBar fields={fields} initial={INITIAL} onApply={(f) => { setFilters(f); load(f); }} testid="so-filter" />
      {err && <div className="mb-3 text-sm text-rose-600" data-testid="so-load-error">{err}</div>}
      <Table testid="so-table" onRowClick={(r) => nav(`/sales-orders/${r.id}`)} empty="Sipariş bulunamadı"
        columns={[
          { label: "Sipariş No", render: (r) => <span className="font-mono text-xs font-semibold" data-testid={`so-row-no-${r.id}`}>{r.so_no}</span> },
          { label: "Müşteri", render: (r) => r.customer_name ? <span className="font-medium">{r.customer_name}</span> : <span className="text-amber-700 font-semibold" data-testid={`so-needs-customer-${r.id}`}>Müşteri eşleşmesi gerekli</span> },
          { label: "Müşteri Ref.", render: (r) => r.customer_ref || "—" },
          { label: "Sipariş Tarihi", render: (r) => trDate(r.order_date) },
          { label: "Teslim Tarihi", render: (r) => trDate(r.delivery_date) },
          { label: "Toplam", render: (r) => <b data-testid={`so-row-total-${r.id}`}>{fmtNum(r.totals?.grand_total)} TRY</b> },
          { label: "Vade", render: (r) => `${r.due_days} gün` },
          { label: "Kaynak", render: (r) => SOURCE_LABEL[r.source] || r.source },
          { label: "Stok Durumu", render: (r) => <StockStatusBadge status={r.stock_status} shortage={r.shortage_total} testid={`so-row-stock-${r.id}`} /> },
          { label: "Teslim Durumu", render: (r) => <DeliveryStatus status={r.status} /> },
          { label: "Durum", render: (r) => <OrderStatusBadge status={r.status} testid={`so-row-status-${r.id}`} /> },
          { label: "İşlemler", render: (r) => <OrderActions so={r} caps={caps} compact onChanged={() => load()} /> },
        ]}
        rows={rows} />
    </div>
  );
}
